import { ArrowUpRight, Megaphone, Star, Gift, Users } from "lucide-react";
import SEO from "@/components/SEO";
import AirtableFormEmbed from "@/components/AirtableFormEmbed";
import { FORMS } from "@/constants/brand";
import { useTranslation } from "@/i18n/LanguageContext";

// Match the providers image used on the Home landing page.
const providersImg = "https://customer-assets.emergentagent.com/job_cc2168ac-14a3-4252-9a7f-505676b149a2/artifacts/5mlvozaq_For%20Providers.jpeg";

const CARD_ICONS = [Megaphone, Star, Gift, Users];

export default function ForServiceProvidersPage() {
    const { t } = useTranslation();
    const cards = t("providers.why.cards");
    const cardList = Array.isArray(cards) ? cards : [];
    const nextSteps = t("providers.form.nextSteps");
    const nextList = Array.isArray(nextSteps) ? nextSteps : [];

    return (
        <div data-testid="for-providers-page">
            <SEO
                title={t("providers.seo.title")}
                description={t("providers.seo.description")}
                keywords="women-owned contractors, women in trades, female contractors, women-owned home service providers, founding provider, get listed, home improvement professionals"
                path="/for-service-providers"
            />

            <section className="bg-cream">
                <div className="container-x pt-20 lg:pt-28 pb-16 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <p className="label-eyebrow">{t("providers.hero.eyebrow")}</p>
                        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading tracking-tight leading-[1.05] text-ink">
                            {t("providers.hero.title1")}{" "}
                            <span className="italic text-terracotta">{t("providers.hero.titleAccent")}</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-lg text-ink-soft leading-relaxed">
                            {t("providers.hero.body")}
                        </p>
                        <div className="mt-8">
                            <a
                                href={FORMS.foundingProvider}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="providers-cta-top"
                                className="inline-flex items-center gap-2 rounded-full bg-ink text-cream font-semibold px-7 py-4 hover:bg-ink-soft transition-colors"
                            >
                                {t("providers.hero.cta")}
                                <ArrowUpRight size={18} />
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-3xl overflow-hidden">
                            <img
                                src={providersImg}
                                alt="A skilled woman service provider in a hard hat planning a renovation with a tablet in hand"
                                className="w-full h-[460px] object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-cream-deep">
                <div className="container-x section-y">
                    <div className="max-w-3xl">
                        <p className="label-eyebrow">{t("providers.why.eyebrow")}</p>
                        <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                            {t("providers.why.title1")}{" "}
                            <span className="italic text-terracotta">{t("providers.why.titleAccent")}</span>
                        </h2>
                    </div>

                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {cardList.map((card, i) => {
                            const Icon = CARD_ICONS[i] || Megaphone;
                            return (
                                <div key={card.title} className="bg-white border border-border rounded-2xl p-7 hover:shadow-md transition-shadow">
                                    <span className="w-11 h-11 rounded-full bg-terracotta/10 flex items-center justify-center text-terracotta">
                                        <Icon size={20} />
                                    </span>
                                    <h3 className="mt-5 font-heading text-lg text-ink">{card.title}</h3>
                                    <p className="mt-2 text-sm text-ink-soft leading-relaxed">{card.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-cream">
                <div className="container-x section-y">
                    <div className="grid lg:grid-cols-12 gap-12 items-start">
                        <div className="lg:col-span-5">
                            <p className="label-eyebrow">{t("providers.form.eyebrow")}</p>
                            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                                {t("providers.form.title1")}{" "}
                                <span className="italic text-terracotta">{t("providers.form.titleAccent")}</span>
                            </h2>
                            <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                                {t("providers.form.body")}
                            </p>
                            <div className="mt-8 rounded-2xl bg-cream-deep border border-border p-6">
                                <p className="label-eyebrow">{t("providers.form.nextTitle")}</p>
                                <ul className="mt-3 space-y-2 text-sm text-ink-soft">
                                    {nextList.map((step) => (
                                        <li key={step}>{step}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div id="provider" className="lg:col-span-7 bg-cream-deep border border-border rounded-3xl p-4 lg:p-6">
                            <AirtableFormEmbed
                                src={FORMS.foundingProvider}
                                title={t("providers.form.formTitle")}
                                testId="providers-form-embed"
                                height={800}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
